import { useCallback, useEffect, useState } from "react";

import { describeActionError } from "../../core-api/errors";
import {
  loadSenderSuppressions,
  releaseSenderSuppression,
  type SenderSuppressionEntry,
} from "./api";

function scopeLabel(scope: SenderSuppressionEntry["scope"]): string {
  return scope === "DOMAIN" ? "公司域名" : "邮箱";
}

function timeLabel(value: string | null): string {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "—" : parsed.toLocaleString("zh-CN");
}

/**
 * Manages the Workspace "do not work with" list. A suppressed sender keeps emailing, but every
 * later inquiry is closed as an invalid inquiry instead of being handed to sales/sourcing, so an
 * operator no longer re-marks the same requester by hand. Release stays admin-only on the server.
 */
export function SenderSuppressionPanel({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [items, setItems] = useState<SenderSuppressionEntry[] | null>(null);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    setError("");
    try {
      const result = await loadSenderSuppressions(signal);
      setItems(result.items);
    } catch (cause) {
      if (signal?.aborted) return;
      setError(describeActionError(cause, "不再合作名单加载失败"));
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [open, load]);

  if (!open) return null;

  const release = async (id: string) => {
    setBusyId(id);
    setError("");
    try {
      await releaseSenderSuppression(id);
      await load();
    } catch (cause) {
      setError(describeActionError(cause, "解除不再合作标记失败"));
    } finally {
      setBusyId(null);
    }
  };

  const activeCount = items?.filter((item) => item.active).length ?? 0;

  return (
    <div className="triage-modal-layer" data-overlay-layer="action-confirmation">
      <button
        type="button"
        className="triage-modal-backdrop"
        aria-label="关闭不再合作名单"
        onClick={onClose}
      />
      <section
        className="triage-modal"
        role="dialog"
        aria-modal="true"
        aria-label="不再合作名单"
      >
        <header>
          <div>
            <span>客户黑名单</span>
            <h2>不再合作名单</h2>
          </div>
          <button
            type="button"
            className="triage-modal-close"
            onClick={onClose}
            aria-label="关闭"
          >
            ×
          </button>
        </header>
        <div className="triage-modal-content">
          <p className="triage-modal-intro">
            名单中的发件人（公司域名或单个邮箱）之后发来的询单，系统会在进入分诊前自动判为
            <strong>无效询单</strong>并关闭，不会创建案件、分配或任务，也不会改动 Gmail
            垃圾标记。解除后该发件人恢复正常判定；名单条目会保留，便于审计。
          </p>
          <p className="triage-modal-status">
            {items ? `共 ${items.length} 条，其中 ${activeCount} 条生效中` : "正在加载…"}
          </p>
          {error ? (
            <p className="triage-modal-error" role="alert">
              {error}
            </p>
          ) : null}
          {items && items.length === 0 ? (
            <p className="triage-modal-status">
              还没有任何发件人。在询单工作台点击“无效询单”时可勾选“同时加入不再合作名单”。
            </p>
          ) : null}
          {items && items.length > 0 ? (
            <table className="triage-suppression-table">
              <thead>
                <tr>
                  <th>标识</th>
                  <th>范围</th>
                  <th>原因</th>
                  <th>加入时间</th>
                  <th>状态</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className={item.active ? "" : "is-released"}>
                    <td>
                      <strong>{item.displayValue}</strong>
                    </td>
                    <td>{scopeLabel(item.scope)}</td>
                    <td>{item.reason ?? "—"}</td>
                    <td>{timeLabel(item.createdAt)}</td>
                    <td>{item.active ? "生效中" : `已解除 · ${timeLabel(item.releasedAt)}`}</td>
                    <td>
                      {item.active ? (
                        <button
                          type="button"
                          className="real-secondary-action"
                          onClick={() => void release(item.id)}
                          disabled={busyId === item.id}
                        >
                          {busyId === item.id ? "解除中…" : "解除"}
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>
      </section>
    </div>
  );
}
